// Pure logic for the promo codes surface, kept out of the component so the
// create form's field coupling and the display rules for an unreadable coupon
// can be unit tested without rendering anything. Same split as
// lib/advertisers.ts.

import type {
  CreatePromoCodeParams,
  PromoCode,
  PromoCodeDiscount,
  PromoCodeDiscountInput,
  PromoCodeDurationInput,
} from "@/lib/admin-api";
import { formatMinorUnits } from "@/lib/money";

/**
 * Shown wherever admin-service returned UNKNOWN, meaning it could not read the
 * coupon behind the code.
 *
 * The rule this exists for: an unreadable discount must never render as a
 * number and never as a default. "0% off" on a code that actually takes £20
 * off every payment reads as a harmless code, and an operator acting on that
 * leaves a live discount running.
 */
export const UNREADABLE_LABEL = "Unavailable";

export type DiscountKindInput = PromoCodeDiscountInput["kind"];

export interface PromoCodeFormState {
  code: string;
  discountKind: DiscountKindInput;
  /** Percent, as typed. Valid above 0 and up to 100. */
  percentOff: string;
  /** Major units, as typed: "5.00" is £5.00, sent as 500 minor units. */
  amountOff: string;
  currency: string;
  duration: PromoCodeDurationInput;
  durationInMonths: string;
  /** Empty means unlimited, which the API spells as 0. */
  maxRedemptions: string;
  /** yyyy-mm-dd from a date input. Empty means the code never expires. */
  expiresOn: string;
}

export function emptyPromoCodeForm(): PromoCodeFormState {
  return {
    code: "",
    discountKind: "PERCENT",
    percentOff: "",
    amountOff: "",
    currency: "gbp",
    duration: "ONCE",
    durationInMonths: "",
    maxRedemptions: "",
    expiresOn: "",
  };
}

function parseNumber(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  return value;
}

/** Above 0 and up to 100 inclusive, matching the contract's bounds. */
export function parsePercentOff(input: string): number | null {
  const value = parseNumber(input);
  if (value === null) return null;
  if (value <= 0 || value > 100) return null;
  return value;
}

/**
 * Major units in, minor units out: "5" and "5.00" both become 500.
 *
 * The operator types the amount they would say out loud. Asking them to type
 * minor units is how 500 gets entered for £500 off.
 */
export function parseAmountOffMinorUnits(input: string): number | null {
  const value = parseNumber(input);
  if (value === null) return null;
  const minorUnits = Math.round(value * 100);
  if (minorUnits <= 0) return null;
  return minorUnits;
}

function parseWholeNumber(input: string): number | null {
  const value = parseNumber(input);
  if (value === null) return null;
  if (!Number.isInteger(value) || value < 0) return null;
  return value;
}

/**
 * A date input's day, as the instant it starts in UTC.
 *
 * `undefined` is "no expiry", which the request omits entirely. `null` is a
 * value that will not parse, which blocks the submit rather than quietly
 * becoming a code that never expires.
 */
export function expiresOnToInstant(input: string): string | null | undefined {
  const trimmed = input.trim();
  if (trimmed === "") return undefined;
  const date = new Date(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/**
 * The typed form as the request body, or null when it is not ready to send.
 *
 * durationInMonths is DERIVED here, never passed through from the input: the
 * contract requires it above zero for REPEATING and exactly 0 for ONCE and
 * FOREVER, so a months value left behind after switching away from REPEATING
 * would be a 400 rather than a harmless extra field.
 */
export function buildCreatePromoCodeRequest(
  form: PromoCodeFormState,
): CreatePromoCodeParams | null {
  const code = form.code.trim().toUpperCase();
  if (code === "") return null;

  let discount: PromoCodeDiscountInput;
  if (form.discountKind === "PERCENT") {
    const percentOff = parsePercentOff(form.percentOff);
    if (percentOff === null) return null;
    discount = { kind: "PERCENT", percentOff };
  } else {
    const amountOffMinorUnits = parseAmountOffMinorUnits(form.amountOff);
    if (amountOffMinorUnits === null) return null;
    const currency = form.currency.trim().toLowerCase();
    if (currency === "") return null;
    discount = { kind: "AMOUNT", amountOffMinorUnits, currency };
  }

  let durationInMonths = 0;
  if (form.duration === "REPEATING") {
    const months = parseWholeNumber(form.durationInMonths);
    if (months === null || months < 1) return null;
    durationInMonths = months;
  }

  const maxRedemptions =
    form.maxRedemptions.trim() === "" ? 0 : parseWholeNumber(form.maxRedemptions);
  if (maxRedemptions === null) return null;

  const expiresAt = expiresOnToInstant(form.expiresOn);
  if (expiresAt === null) return null;

  return {
    code,
    discount,
    duration: form.duration,
    durationInMonths,
    maxRedemptions,
    ...(expiresAt === undefined ? {} : { expiresAt }),
  };
}

export function canSubmitPromoCode(form: PromoCodeFormState): boolean {
  return buildCreatePromoCodeRequest(form) !== null;
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2 }).format(value);
}

/**
 * Every path that is not a discount this code can state renders the
 * placeholder. That includes a PERCENT with no percentage and an AMOUNT with
 * no amount: a kind without its figure is the same unreadable coupon that
 * UNKNOWN describes, and rendering the missing field would print a zero.
 */
export function discountLabel(discount: PromoCodeDiscount | undefined): string {
  if (!discount) return UNREADABLE_LABEL;

  if (discount.kind === "PERCENT") {
    const percentOff = discount.percentOff;
    if (typeof percentOff !== "number" || !Number.isFinite(percentOff) || percentOff <= 0) {
      return UNREADABLE_LABEL;
    }
    return `${formatPercent(percentOff)}% off`;
  }

  if (discount.kind === "AMOUNT") {
    const amount = discount.amountOffMinorUnits;
    if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) {
      return UNREADABLE_LABEL;
    }
    return `${formatMinorUnits(amount, discount.currency)} off`;
  }

  return UNREADABLE_LABEL;
}

/** REPEATING without a month count is unreadable, not "0 months". */
export function durationLabel(code: Pick<PromoCode, "duration" | "durationInMonths">): string {
  if (code.duration === "ONCE") return "Once";
  if (code.duration === "FOREVER") return "Forever";

  if (code.duration === "REPEATING") {
    const months = code.durationInMonths;
    if (typeof months !== "number" || !Number.isFinite(months) || months < 1) {
      return UNREADABLE_LABEL;
    }
    return months === 1 ? "1 month" : `${months} months`;
  }

  return UNREADABLE_LABEL;
}

export function redemptionsLabel(
  code: Pick<PromoCode, "timesRedeemed" | "maxRedemptions">,
): string {
  const redeemed = Number.isFinite(code.timesRedeemed) ? code.timesRedeemed : 0;
  const max = Number.isFinite(code.maxRedemptions) ? code.maxRedemptions : 0;
  return max > 0 ? `${redeemed} of ${max}` : `${redeemed}, no limit`;
}

export function promoCodeStatusLabel(code: Pick<PromoCode, "active">): string {
  return code.active ? "Active" : "Inactive";
}

export function formatPromoCodeDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}
